import type { AppStatus, CheckState } from '../types';
import './StatusPanel.css';

function mark(state: CheckState): string {
  if (state === 'ok') return '✓';
  if (state === 'error') return '✗';
  if (state === 'running') return '…';
  return '○';
}

function cls(state: CheckState): string {
  return `sp-mark sp-mark--${state}`;
}

export function StatusPanel({
  status,
  onRetry,
  compact = false,
}: {
  status: AppStatus;
  onRetry: () => void;
  /** When true, render as collapsible technical block (Phase 2 main UI). */
  compact?: boolean;
}) {
  const folderLabel =
    status.folder === 'ok'
      ? status.folderAction === 'created'
        ? 'Ordner erstellt'
        : 'Ordner bereit'
      : status.folder === 'running'
        ? 'wird geprüft…'
        : status.folder === 'error'
          ? 'Fehler'
          : 'ausstehend';

  const checks = (
    <ul className="sp-checks">
      <li>
        <span className={cls(status.officeReady)}>{mark(status.officeReady)}</span>
        <span>Outlook</span>
        <span className="sp-check-detail">
          {status.officeReady === 'ok' ? 'Bereit' : status.officeReady === 'running' ? 'lädt…' : '—'}
        </span>
      </li>
      <li>
        <span className={cls(status.auth)}>{mark(status.auth)}</span>
        <span>Microsoft 365</span>
        <span className="sp-check-detail">
          {status.auth === 'ok'
            ? status.authMethod === 'naa-interactive'
              ? 'Angemeldet (Consent)'
              : 'Angemeldet'
            : status.auth === 'running'
              ? 'SSO…'
              : '—'}
        </span>
      </li>
      <li>
        <span className={cls(status.user)}>{mark(status.user)}</span>
        <span>Benutzer erkannt</span>
        <span className="sp-check-detail">{status.user === 'ok' ? 'Graph /me' : '—'}</span>
      </li>
      <li>
        <span className={cls(status.onedrive)}>{mark(status.onedrive)}</span>
        <span>OneDrive</span>
        <span className="sp-check-detail">
          {status.onedrive === 'ok' ? 'Verbunden' : status.onedrive === 'running' ? 'prüft…' : '—'}
        </span>
      </li>
      <li>
        <span className={cls(status.folder)}>{mark(status.folder)}</span>
        <span>SignatureApp</span>
        <span className="sp-check-detail">{folderLabel}</span>
      </li>
    </ul>
  );

  if (compact) {
    return (
      <details className="sp-tech">
        <summary>Technische Informationen</summary>
        <div className="sp-tech-body">
          <div className="sp-block">
            <div className="sp-label">Benutzer</div>
            <div className="sp-value">{status.graphUser?.displayName || '—'}</div>
          </div>
          <div className="sp-block">
            <div className="sp-label">E-Mail</div>
            <div className="sp-value">
              {status.graphUser?.mail || status.graphUser?.userPrincipalName || '—'}
            </div>
          </div>
          {checks}
          {status.error ? (
            <div className="sp-error" role="alert">
              <strong>Fehler</strong>
              <p>{status.error}</p>
              {status.errorDetail ? <pre className="sp-error-detail">{status.errorDetail}</pre> : null}
              <button type="button" className="sp-btn sp-btn--secondary" onClick={onRetry}>
                Erneut prüfen
              </button>
            </div>
          ) : null}
        </div>
      </details>
    );
  }

  return (
    <div className="sp">
      <header className="sp-header">
        <div className="sp-logo" aria-hidden>
          <img src="/logo-256.png" alt="" width={44} height={44} />
        </div>
        <div>
          <h1 className="sp-title">Signatur</h1>
          <p className="sp-subtitle">Systemprüfung</p>
        </div>
      </header>

      <div className="sp-divider" />

      <section className="sp-block">
        <div className="sp-label">Benutzer</div>
        <div className="sp-value">{status.graphUser?.displayName || '—'}</div>
      </section>

      <section className="sp-block">
        <div className="sp-label">E-Mail</div>
        <div className="sp-value">{status.graphUser?.mail || status.graphUser?.userPrincipalName || '—'}</div>
      </section>

      {checks}

      {status.error ? (
        <div className="sp-error" role="alert">
          <strong>Fehler</strong>
          <p>{status.error}</p>
          {status.errorDetail ? <pre className="sp-error-detail">{status.errorDetail}</pre> : null}
          <button type="button" className="sp-btn sp-btn--secondary" onClick={onRetry}>
            Erneut prüfen
          </button>
        </div>
      ) : null}

      {!status.error && status.folder === 'ok' ? (
        <div className="sp-success">Initialisierung erfolgreich…</div>
      ) : null}
    </div>
  );
}
